/**
 * Webmail's mail calls: `/api/mail/*` on this origin, served by the BFF with
 * the user's own token. Every call carries X-Genworx-Request, which the BFF
 * requires for anything that changes state.
 */

import type {
  CreateFolderResult,
  DeleteFolderResult,
  EmptyFolderResult,
  MoveFolderResult,
  RenameFolderResult,
  AttachmentLink,
  BulkRequest,
  BulkResult,
  DeletedMail,
  DraftRequest,
  OutgoingAttachment,
  Folder,
  MailSettings,
  MessageDetail,
  MessagePage,
  MessageSummary,
  QuotaUsage,
  RestoreDeletedRequest,
  RestoreDeletedResult,
  RuleRunRequest,
  RuleRunResult,
  SendRequest,
  SentRecipient,
  SnoozedMessage,
} from 'zitimail-shared-types';

import { fetchOrExplain, parseJson, plainFailure } from './http';

/** A dropped connection, as a MailError with status 0. */
async function reach(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetchOrExplain(path, init, 'mail service');
  } catch (error) {
    throw error instanceof DOMException ? error : new MailError(0, (error as Error).message);
  }
}

export class MailError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'MailError';
  }
}

async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const response = await reach(path, {
    method: init.method ?? 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Genworx-Request': '1',
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });
  const payload = parseJson(await response.text());
  if (!response.ok) {
    const message = (payload as { message?: string } | undefined)?.message;
    if (response.status === 401) {
      window.dispatchEvent(new Event('genworx:session-ended'));
    }
    throw new MailError(response.status, message ?? plainFailure('mail service', response.status));
  }
  return payload as T;
}

const q = (params: Record<string, string | number | undefined>) =>
  new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== '')
      .map(([k, v]) => [k, String(v)]),
  ).toString();

export interface ScheduledMessage {
  readonly id: string;
  readonly uid: string;
  readonly subject: string;
  readonly to: readonly string[];
  readonly sendAt: string;
  readonly state: 'scheduled' | 'sending' | 'sent' | 'failed';
  readonly error?: string;
}

export interface ScheduledList {
  readonly available: boolean;
  /** The user has allowed scheduled sending (a delegation is held). */
  readonly allowed: boolean;
  readonly messages: readonly ScheduledMessage[];
}

/**
 * Asks the user, once, to allow scheduled sending: a small window signs in
 * silently and hands the BFF a delegation for mail only. Resolves true when
 * it was given.
 */
export function allowScheduling(): Promise<boolean> {
  return new Promise((resolve) => {
    const popup = window.open('/auth/grant', 'genworx-grant', 'width=460,height=560');
    if (!popup) {
      // The desktop app opens no window here: it opens the server's one-time
      // link in the person's own browser instead (ADR-0051).
      void allowedInBrowser().then(resolve);
      return;
    }
    const done = (ok: boolean) => {
      window.removeEventListener('message', listener);
      window.clearInterval(watch);
      resolve(ok);
    };
    const listener = (e: MessageEvent) => {
      const data = e.data as { type?: string; ok?: boolean } | null;
      if (e.origin === window.location.origin && data?.type === 'genworx:grant') {
        done(Boolean(data.ok));
      }
    };
    window.addEventListener('message', listener);
    // Closed without an answer: not allowed.
    const watch = window.setInterval(() => popup.closed && done(false), 500);
  });
}

/**
 * The desktop app's way: the link opens in the system browser, and the
 * server is asked until it holds the delegation. In webmail, where a blocked
 * popup also lands here, nothing is opened and the answer is no.
 */
async function allowedInBrowser(): Promise<boolean> {
  try {
    const started = await call<{ opened?: boolean }>('/api/mail/grant', {
      method: 'POST',
      body: {},
    });
    if (!started.opened) {
      return false;
    }
  } catch {
    return false;
  }
  const deadline = Date.now() + 5 * 60_000;
  while (Date.now() < deadline) {
    await new Promise((r) => window.setTimeout(r, 2000));
    try {
      if ((await call<ScheduledList>('/api/mail/scheduled')).allowed) {
        return true;
      }
    } catch {
      // Asked again in a moment.
    }
  }
  return false;
}

/** An upload in progress: its result, and a way to stop it. */
export interface LinkUpload {
  readonly done: Promise<AttachmentLink>;
  readonly abort: () => void;
}

/**
 * Sends a large file to the BFF, which streams it to the API and answers with
 * its download link. XMLHttpRequest rather than fetch: it reports upload
 * progress. An aborted upload rejects with an `AbortError`.
 */
export function uploadAttachmentLink(
  file: File,
  onProgress: (fraction: number) => void,
): LinkUpload {
  const xhr = new XMLHttpRequest();
  const done = new Promise<AttachmentLink>((resolve, reject) => {
    xhr.open('POST', '/api/mail/attachment-links');
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('X-Genworx-Request', '1');
    xhr.setRequestHeader('X-Filename', encodeURIComponent(file.name));
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(e.loaded / e.total);
      }
    };
    xhr.onload = () => {
      let payload: unknown;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        payload = undefined;
      }
      if (xhr.status === 201) {
        resolve(payload as AttachmentLink);
        return;
      }
      if (xhr.status === 401) {
        window.dispatchEvent(new Event('genworx:session-ended'));
      }
      const message =
        xhr.status === 401
          ? 'your session has ended'
          : (payload as { message?: string } | undefined)?.message;
      reject(new MailError(xhr.status, message ?? 'the upload did not go through'));
    };
    xhr.onerror = () => reject(new MailError(0, 'the connection was lost'));
    xhr.onabort = () => reject(new DOMException('The upload was cancelled.', 'AbortError'));
    xhr.send(file);
  });
  return { done, abort: () => xhr.abort() };
}

export const mail = {
  folders: () => call<Folder[]>('/api/mail/folders'),
  renameFolder: (path: string, name: string) =>
    call<RenameFolderResult>('/api/mail/folders/rename', { method: 'POST', body: { path, name } }),
  /** Into another of the person's folders, or to the top (no parent). */
  moveFolder: (path: string, parent?: string) =>
    call<MoveFolderResult>('/api/mail/folders/move', {
      method: 'POST',
      body: parent ? { path, parent } : { path },
    }),
  /** Its mail goes to Trash first. */
  deleteFolder: (path: string) =>
    call<DeleteFolderResult>('/api/mail/folders/delete', { method: 'POST', body: { path } }),
  /** Trash or Junk, all of it, deleted for good (recoverable for a while). */
  emptyFolder: (path: string) =>
    call<EmptyFolderResult>('/api/mail/folders/empty', { method: 'POST', body: { path } }),
  /** A folder of the person's own, on the server: every mail app sees it. */
  createFolder: (name: string, parent?: string) =>
    call<CreateFolderResult>('/api/mail/folders', {
      method: 'POST',
      body: { name, ...(parent ? { parent } : {}) },
    }),
  messages: (folder: string, cursor?: string, search?: string) =>
    call<MessagePage>(`/api/mail/messages?${q({ folder, cursor, q: search })}`),
  message: (folder: string, uid: string, images = false) =>
    call<MessageDetail>(`/api/mail/message?${q({ folder, uid, images: images ? 1 : undefined })}`),
  attachmentUrl: (folder: string, uid: string, part: string, view = false) =>
    `/api/mail/attachment?${q({ folder, uid, part, view: view ? 1 : undefined })}`,
  /** The message exactly as stored, headers and all, for troubleshooting. */
  source: async (folder: string, uid: string): Promise<string> => {
    const response = await fetch(`/api/mail/source?${q({ folder, uid })}`, {
      credentials: 'include',
      headers: { 'X-Genworx-Request': '1' },
    });
    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new Event('genworx:session-ended'));
      }
      const payload = (await response.json().catch(() => undefined)) as
        { message?: string } | undefined;
      throw new MailError(
        response.status,
        payload?.message ?? `The mail service answered ${response.status}.`,
      );
    }
    return response.text();
  },
  /** The same, as an .eml file to save. */
  sourceUrl: (folder: string, uid: string) => `/api/mail/source?${q({ folder, uid, download: 1 })}`,
  flags: (folder: string, uid: string, flags: { seen?: boolean; flagged?: boolean }) =>
    call('/api/mail/flags', { method: 'POST', body: { folder, uid: Number(uid), ...flags } }),
  bulk: (request: BulkRequest) =>
    call<BulkResult>('/api/mail/bulk', { method: 'POST', body: request }),
  /** The conversation's messages in the Inbox, Sent and Archive, other than `folder`. */
  related: (folder: string, ids: readonly string[]) =>
    call<{ messages: MessageSummary[] }>('/api/mail/related', {
      method: 'POST',
      body: { folder, ids },
    }),
  /** Mail deleted for good that the server still keeps (ADR-0032). */
  deleted: () => call<DeletedMail>('/api/mail/deleted'),
  restoreDeleted: (request: RestoreDeletedRequest) =>
    call<RestoreDeletedResult>('/api/mail/deleted/restore', { method: 'POST', body: request }),
  move: (folder: string, uid: string, to: string) =>
    call('/api/mail/move', { method: 'POST', body: { folder, uid: Number(uid), to } }),
  remove: (folder: string, uid: string) =>
    call('/api/mail/delete', { method: 'POST', body: { folder, uid: Number(uid) } }),
  /** `queued`: the desktop app had no connection and sends it when it has. */
  send: (request: SendRequest) =>
    call<{ sent: boolean; queued?: boolean }>('/api/mail/send', { method: 'POST', body: request }),
  schedule: (request: SendRequest, sendAt: Date) =>
    call<{ id: string; sendAt: string }>('/api/mail/schedule', {
      method: 'POST',
      body: { ...request, sendAt: sendAt.toISOString() },
    }),
  scheduled: () => call<ScheduledList>('/api/mail/scheduled'),
  cancelScheduled: (id: string) =>
    call<{ draftUid?: string }>('/api/mail/scheduled/cancel', { method: 'POST', body: { id } }),
  revokeScheduling: () => call('/api/mail/scheduled/revoke', { method: 'POST', body: {} }),
  /** Out of sight until `until`, then back in the Inbox unread; 428 until allowed. */
  snooze: (folder: string, uid: string, until: Date) =>
    call<{ id: string; until: string }>('/api/mail/snooze', {
      method: 'POST',
      body: { folder, uid, until: until.toISOString() },
    }),
  snoozed: () => call<{ messages: SnoozedMessage[] }>('/api/mail/snoozed'),
  wakeSnoozed: (id: string) => call('/api/mail/snoozed/wake', { method: 'POST', body: { id } }),
  saveDraft: (draft: DraftRequest) =>
    call<{ saved: boolean; uid?: string; folder: string }>('/api/mail/draft', {
      method: 'POST',
      body: draft,
    }),
  /**
   * The last save as the page goes away: sent with `keepalive` so it is not
   * cancelled with the page. Browsers cap such a request at 64 KB, so a draft
   * with attachments too big for that is left to the saves made while typing.
   */
  saveDraftOnLeaving: (draft: DraftRequest): void => {
    const body = JSON.stringify(draft);
    if (body.length > 60_000) {
      return;
    }
    void fetch('/api/mail/draft', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        Accept: 'application/json',
        'X-Genworx-Request': '1',
        'Content-Type': 'application/json',
      },
      body,
    }).catch(() => undefined);
  },
  /** Saves a message as a template, in the Templates folder (Phase 11). */
  saveTemplate: (template: DraftRequest) =>
    call<{ saved: boolean; uid?: string; folder: string }>('/api/mail/template', {
      method: 'POST',
      body: template,
    }),
  draftAttachments: (uid: string) =>
    call<Array<OutgoingAttachment & { size: number }>>(`/api/mail/draft/attachments?${q({ uid })}`),
  /** Who the person wrote to in the last six months, from Sent (migration 0041). */
  sentRecipients: () => call<{ people: SentRecipient[] }>('/api/mail/sent-recipients'),
  discardDraft: (uid: string) =>
    call('/api/mail/draft/discard', { method: 'POST', body: { uid: Number(uid) } }),
  quota: () => call<QuotaUsage>('/api/mail/quota'),
  settings: () => call<MailSettings & { managed: boolean }>('/api/mail/settings'),
  saveSettings: (settings: MailSettings) =>
    call('/api/mail/settings', { method: 'PUT', body: settings }),
  /** Applies a rule, saved or not, to mail already in a folder. */
  runRule: (request: RuleRunRequest) =>
    call<RuleRunResult>('/api/mail/rules/run', { method: 'POST', body: request }),
};
